import React, { useState, useEffect, useRef } from "react";
import "../../styles/PagesStyles/Services.css";
import usePageTitle from "../../hooks/usePageTitle";
import { trackEvent } from "../../utils/google-analytics";

const Services = () => {

  usePageTitle("Combat Solutions AI | Services");

  const [activeService, setActiveService] = useState(0);
  const heroRef = useRef(null);
  const badgeRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const imageRef = useRef(null);
  const serviceTitleRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const services = [
    {
  title: "Agentic AI Systems",
  icon: <i className="bi bi-box"></i>,
  description:
    "Collaborative, governed Generative AI agents designed to plan, execute, and validate complex enterprise workflows with human oversight, security, and compliance built in.",
  agents: ["Orchestrator", "Planner", "Executor", "Verifier"],
  features: [
    "Multi-agent architecture for complex task orchestration",
    "LLM-powered reasoning with enterprise data (RAG-based)",
    "Human-in-the-loop control for accuracy, accountability, and risk management",
    "Auditability, traceability, and decision explainability",
    "Designed for risk, compliance, tenders, and operations",
  ],
},
{
  title: "AI-Powered Workflow Automation",
  icon: <i className="bi bi-gear"></i>,
  description:
    "Intelligent, adaptive automation that connects systems, data, and decisions optimizing enterprise workflows in real time with built-in governance and scalability.",
  agents: ["Analyzer", "Optimizer", "Executor"],
  features: [
    "Intelligent process automation across structured and unstructured workflows",
    "Real-time workflow optimization driven by AI insights and feedback loops",
    "Seamless cross-platform integration with enterprise systems and APIs",
    "Adaptive learning that continuously improves performance and accuracy",
    "Human-in-the-loop controls for reliability and compliance",
  ],
},

{
  title: "Enterprise AI Copilots",
  icon: <i className="bi bi-text-paragraph"></i>,
  description:
    "Role-aware, AI-powered assistants that augment teams by delivering contextual intelligence, automating routine tasks, and enabling faster, better-informed decisions across enterprise workflows.",
  agents: ["Assistant", "Reviewer", "Advisor"],
  features: [
    "Context-aware assistance grounded in enterprise data and workflows",
    "Domain-specific knowledge with secure, role-based access controls",
    "Seamless integration into existing tools and business processes",
    "Human-in-the-loop review for accuracy, accountability, and compliance",
    "Continuous learning through structured user feedback",
  ],
},

{
  title: "Regulated-Industry AI Solutions",
  icon: <i className="bi bi-lock"></i>,
  description:
    "Governed, compliant Generative AI systems designed for regulated environments delivering transparency, control, and security across every AI-driven decision.",
  agents: ["Monitor", "Validator", "Auditor"],
  features: [
    "Regulatory-ready AI architectures aligned with industry standards and policies",
    "End-to-end auditability with complete decision and data traceability",
    "Explainable AI outputs to support compliance, reviews, and approvals",
    "Enterprise-grade security controls including access management and data protection",
    "Continuous monitoring for risk, drift, and policy adherence",
  ],
},
{
  title: "Enterprise GenAI Platforms",
  icon: <i className="bi bi-layers"></i>,
  description:
    "Robust, production-grade GenAI platforms designed for enterprises providing the foundation to build, deploy, and scale AI agents, copilots, and workflow automation with governance, security, and observability built in.",
  agents: ["Orchestrator", "Builder", "Operator"],
  features: [
    "End-to-end AI lifecycle management including model training, deployment, and monitoring",
    "Secure, governed multi-tenant architecture designed for enterprise scalability",
    "Integration-ready with ERP, CRM, DMS, and custom enterprise APIs",
    "Full observability with logs, metrics, and traceable, auditable decision-making",
    "Plug-and-play deployment of AI agents and workflows with human-in-the-loop controls",
 
  ],
}
  ];
  
const GenaiServices = {
  Gen: [
    {
      icon: "bi-diagram-3",
      title: "AI Strategy & Consulting",
      desc: "Enterprise AI roadmap and ROI-driven planning",
    },
    {
      icon: "bi-cpu",
      title: "Custom GenAI & Agentic Systems",
      desc: "Domain-specific GenAI and autonomous agents",
    },
    {
      icon: "bi-gear-wide-connected",
      title: "Enterprise AI Automation",
      desc: "Intelligent automation of processes using GenAI",
    },
    {
      icon: "bi-database-check",
      title: "LLM Engineering & RAG Solutions",
      desc: "Model optimization, fine-tuning, and secure enterprise systems",
    },
    {
      icon: "bi-layers",
      title: "AI Platform, Integration & SaaS Development",
      desc: "Scalable AI platforms and SaaS products",
    },
    {
      icon: "bi-shield-lock",
      title: "AI Security & MLOps",
      desc: "Secure deployment, compliance, and ongoing AI operations",
    },
  ],
};

  useEffect(() => {
    trackEvent("page_view", {
      page: "Services Page",
    });
  }, []);


  useEffect(() => {
    // Mouse move effect for hero section
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Handle service card click
  const handleServiceClick = (index) => {
    setActiveService(index);

    trackEvent("service_select", {
      page: "Services Page",
      service_name: services[index].title,
    });

    if (window.innerWidth <= 1024) {
      setTimeout(() => {
        if (rightPanelRef.current) {
          window.scrollTo({
            top: rightPanelRef.current.offsetTop - 100,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  return (
    <div className="services-container">
      {/* Hero Section */}
      <section ref={heroRef} className="services-hero-section">
        <div className="services-hero-content">
          <div className="services-hero-left">
            <div ref={badgeRef} className="badge">
              <i className="bi bi-star-fill"></i> Enterprise AI Solutions
            </div>
            <h1 ref={titleRef} className="services-hero-title">
              AI Systems We Deliver
            </h1>
            <p ref={descRef} className="services-hero-description">
              Governed Generative AI systems built to integrate seamlessly with enterprise
              workflows, enabling intelligent automation, explainable decisions, and scalable
              operations without compromising security or control.
            </p>
          </div>
          <div ref={imageRef} className="services-hero-right">
            {/* Add your hero image here */}
            <div className="hero-image-placeholder">
              {/* Placeholder for hero image */}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="services-header">
          <h2 className="services-title">Our GenAI Services</h2>
          <p className="services-subtitle">
            Enterprise grade Generative AI services designed for real business
            workflows.
          </p>
        </div>

        <div className="Gen-grid">
          {GenaiServices.Gen.map((item, index) => (
            <div className="Gen-card" key={index}>
              <i className={`bi ${item.icon} gen-icon`}></i>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>

                 {/* Separator Line */}
      <div className="Gen-section-separator">
        <div className="Gen-separator-line"></div>
      </div>

        <div className="services-grid">
          {/* Left Panel */}
          <div ref={leftPanelRef} className="left-panel">
            {services.map((service, index) => (
              <div
                key={index}
                onClick={() => handleServiceClick(index)}
                onMouseEnter={() => {
                  if (window.innerWidth > 1024) {
                    handleServiceClick(index);
                    trackEvent("service_hover", {
                      page: "Services Page",
                      service_name: service.title,
                    });
                  }
                }} className={`service-card ${activeService === index ? 'active' : ''}`}
              >
                <div className="service-card-content">
                  <div className={`service-icon ${activeService === index ? 'active' : ''}`}>
                    {service.icon}
                  </div>
                  <div className="service-info">
                    <h3 className="service-title">{service.title}</h3>
                    
                  </div>
                </div>
                {/* Hover effect line */}
                <div className={`hover-line ${activeService === index ? 'active' : ''}`}></div>
              </div>
            ))}
          </div>

          {/* Right Panel */}
          <div ref={rightPanelRef} className="right-panel">
            <div className="detail-card">
              <div className="detail-header">
                <div className="detail-icon">
                  {services[activeService].icon}
                </div>
                <h3 className="detail-title">
                  {services[activeService].title}
                </h3>
              </div>

              <p className="detail-description">
                {services[activeService].description}
              </p>

              {/* Agent Cards */}
              <div className="agents-container">
                {services[activeService].agents.map((agent, i) => (
                  <div
                    key={`${activeService}-${i}`}
                    className="agent-card"
                    onClick={() =>
                      trackEvent("agent_interaction", {
                        page: "Services Page",
                        service_name: services[activeService].title,
                        agent: agent,
                      })
                    }
                  >
                    <span className="agent-icon">✦</span>
                    {agent}
                  </div>
                ))}
              </div>

              {/* Features */}
              <div className="features-container">
                {services[activeService].features && services[activeService].features.map((feat, idx) => (
                  <div key={idx} className="feature-item">
                    <div className="check-icon">✓</div>
                    <div className="feature-text">{feat}</div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
  
export default Services;
